import wolfieLogo from '@assets/logos/mrwolf-hub-avatar.png';
import WolfAvatar from '@components/ui/WolfAvatar';
import { KeywordIcon24, MicOffIcon24 } from '@components/ui/WolfIcons';
import WolfTooltip from '@components/ui/WolfTooltip';
import { getSessionUuid, getTokenLocalStorage, getTokenSessionStorage } from '@config/storage';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import { ArrowDownward, ArrowUpward, ChevronRight } from '@mui/icons-material';
import { Box, CircularProgress, IconButton } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { setWolfieLoading, setWolfieMinimized, setWolfieSpeaking } from '@redux/reducers/wolfie.reducer';
import { RootState } from '@redux/store';
import { blobToBase64 } from '@utils/blobToBase64';
import { useEffect, useRef, useState } from 'react';

// Add type definitions for Web Speech API
interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onerror: ((this: SpeechRecognition, ev: SpeechRecognitionErrorEvent) => any) | null;
  onresult: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => any) | null;
  start(): void;
  stop(): void;
}

declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognition;
    webkitSpeechRecognition: new () => SpeechRecognition;
  }
}

const WolfieHub = ({
  isTalking,
  onSubmit,
  onTogglePosition,
  isOnBottom = true,
  //   onRecording,
}: {
  isTalking: boolean;
  onSubmit: (text: string) => void;
  onTogglePosition?: () => void;
  isOnBottom?: boolean;
  //   onRecording: (recording: boolean) => void;
}) => {
  const { avatar, firstName, rememberMe } = useAppSelector((state: RootState) => state.user);
  const {
    minimized: wolfieMinimized,
    speaking: wolfieSpeaking,
    autoEnableMic,
    messages,
    loading,
    wakeWordEnabled,
  } = useAppSelector((state: RootState) => state.wolfie);

  const localeWolfie = useTranslation('wolfie');

  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const audioChunks = useRef<Blob[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const silenceTimer = useRef<NodeJS.Timeout | null>(null);
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  // const [listening, setListening] = useState(false);
  const [isChatMinimized, setIsChatMinimized] = useState(wolfieMinimized);
  const [talkEnabled, setTalkEnabled] = useState(wolfieSpeaking);
  const [microphoneEnabled, setMicrophoneEnabled] = useState(false);
  const [newMessage, setNewMessage] = useState(false);

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (talkEnabled || !wolfieMinimized) {
      return;
    } else if (messages.length > 0 && messages[messages.length - 1].role === 'assistant' && isChatMinimized) {
      setNewMessage(true);
    }
  }, [messages, wolfieMinimized, isChatMinimized, talkEnabled]);

  useEffect(() => {
    if (wolfieMinimized && !wolfieSpeaking) {
      setNewMessage(false);
      setIsChatMinimized(true);
    }
  }, [wolfieMinimized, wolfieSpeaking]);

  useEffect(() => {
    if (talkEnabled && autoEnableMic && !isTalking && microphoneEnabled) {
      dispatch(
        appendActionMessage({
          message: localeWolfie['waitingForReply'],
          type: SnackType.DARK,
          duration: 2000,
        }),
      );
      setTimeout(() => {
        startRecording();
      }, 2000);
    }
    if (wakeWordEnabled && !isTalking && microphoneEnabled) {
      console.log('wakeWordEnabled', wakeWordEnabled);
      startWakeWordDetection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTalking, wolfieMinimized, wolfieSpeaking, autoEnableMic, talkEnabled]);

  const toggleChat = () => {
    setNewMessage(false);
    const newState = !wolfieMinimized;
    dispatch(setWolfieMinimized(newState));
    setIsChatMinimized(newState);
  };

  // const toggleTalk = () => {
  //   if (wolfieSpeaking) {
  //     speechSynthesis.cancel();
  //   }
  //   setTalkEnabled((prev) => !prev);
  //   dispatch(setWolfieSpeaking(!wolfieSpeaking));
  // };

  // const manuallyStartRecording = async () => {
  //   try {
  //     // First check if the browser supports getUserMedia
  //     if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
  //       dispatch(
  //         appendActionMessage({
  //           message: localeWolfie['microphoneNotSupported'],
  //           type: SnackType.ERROR,
  //           duration: 5000,
  //         }),
  //       );
  //       return;
  //     }

  //     // Check for microphone permission
  //     const permission = await navigator.permissions.query({ name: 'microphone' as PermissionName });

  //     if (permission.state === 'denied') {
  //       dispatch(
  //         appendActionMessage({
  //           message: localeWolfie['microphoneBlocked'],
  //           type: SnackType.ERROR,
  //           duration: 5000,
  //         }),
  //       );
  //       return;
  //     }

  //     // If we get here, either permission is granted or prompt will be shown
  //     startRecording();
  //   } catch (error) {
  //     console.error('Error checking microphone permissions:', error);
  //     dispatch(
  //       appendActionMessage({
  //         message: localeWolfie['microphonePermissionError'],
  //         type: SnackType.ERROR,
  //         duration: 5000,
  //       }),
  //     );
  //   }
  // };

  const startRecording = async () => {
    setMicrophoneEnabled(true);
    // setListening(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorder.current = new MediaRecorder(stream);

      audioContextRef.current = new AudioContext();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      source.connect(analyserRef.current);

      mediaRecorder.current.ondataavailable = (event) => {
        audioChunks.current.push(event.data);
      };

      mediaRecorder.current.onstop = () => {
        const audioBlob = new Blob(audioChunks.current, { type: 'audio/wav' });
        audioChunks.current = [];
        transcribeAudio(audioBlob);
        // Stop all tracks to free up the mic
        stream.getTracks().forEach((track) => track.stop());

        if (audioContextRef.current) {
          audioContextRef.current.close();
        }
      };

      mediaRecorder.current.start();
      detectSilence(analyserRef.current, stopRecording);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      dispatch(
        appendActionMessage({
          message: localeWolfie['microphoneAccessError'],
          type: SnackType.ERROR,
          duration: 5000,
        }),
      );
    }
  };

  // const manuallyStopRecording = () => {
  //   setMicrophoneEnabled(false);
  //   stopRecording();
  // };

  const stopRecording = () => {
    // setListening(false);
    if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
      mediaRecorder.current.stop();
    }
  };

  const detectSilence = (analyser: AnalyserNode, callback: () => void, silenceDelay = 10000, threshold = 35) => {
    const buffer = new Uint8Array(analyser.fftSize);

    const checkSilence = () => {
      analyser.getByteFrequencyData(buffer);
      const avgVolume = buffer.reduce((sum, val) => sum + val, 0) / buffer.length;

      if (avgVolume < threshold) {
        if (!silenceTimer.current) {
          silenceTimer.current = setTimeout(() => {
            callback();
            silenceTimer.current = null;
          }, silenceDelay);
        }
      } else {
        if (silenceTimer.current) {
          clearTimeout(silenceTimer.current);
          silenceTimer.current = null;
        }
      }

      if (mediaRecorder.current?.state === 'recording') {
        requestAnimationFrame(checkSilence);
      }
    };

    checkSilence();
  };

  async function transcribeAudio(audioBlob: Blob) {
    try {
      dispatch(setWolfieLoading(true));
      const token = rememberMe ? getTokenLocalStorage() : getTokenSessionStorage();
      const base64Audio = await blobToBase64(audioBlob);
      const response = await fetch(process.env.REACT_APP_BACKEND_URL + 'ai/transcribeAudio', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'Session-Uuid': getSessionUuid(),
        },
        body: JSON.stringify({
          audioBase64: base64Audio,
        }),
      });

      const result = await response.json();
      if (response.ok) {
        const transcription = result.transcription;
        onSubmit(transcription);
      } else {
        console.error('Backend error:', result.message);
      }
    } catch (error) {
      dispatch(setWolfieLoading(false));
      console.error('Error transcribing audio:', error);
    }
  }

  // TODO: We may need to further refine this, this was just a quick implementation
  const startWakeWordDetection = async () => {
    console.log('startWakeWordDetection');
    try {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        console.error('Speech recognition not supported in this browser');
        return;
      }

      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US'; // You might want to make this configurable

      recognition.onresult = (event) => {
        const transcript = event.results[event.results.length - 1][0].transcript.toLowerCase();
        if (transcript.includes('wolf') || transcript.includes('mr wolf')) {
          dispatch(
            appendActionMessage({
              message: localeWolfie['wolfieGreeting'],
              type: SnackType.DARK,
              duration: 2000,
            }),
          );
          stopWakeWordDetection();
          startRecording();
        }
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        if (event.error === 'no-speech') {
          // Restart recognition if it stops due to no speech
          stopWakeWordDetection();
        }
      };

      recognitionRef.current = recognition;
      recognition.start();
    } catch (error) {
      console.error('Error starting wake word detection:', error);
    }
  };

  const stopWakeWordDetection = () => {
    if (recognitionRef.current) {
      console.log('stopWakeWordDetection');
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
  };

  return (
    <Box className="flex items-center justify-between gap-5 bg-frozenGrayShades9 px-4 py-2 rounded-full shadow-shadow3modals w-80 h-18">
      <Box className="flex items-center gap-2">
        {/* Position Toggle Arrow */}
        {onTogglePosition && (
          <WolfTooltip title={isOnBottom ? localeWolfie['moveToTop'] || 'Move to top' : localeWolfie['moveToBottom'] || 'Move to bottom'}>
            <IconButton
              onClick={onTogglePosition}
              className="!p-1 !bg-frozenGrayShades8 hover:!bg-frozenGrayShades6 rounded-full !text-white"
              size="small">
              {isOnBottom ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
            </IconButton>
          </WolfTooltip>
        )}

        {/* Mr. Wolf Logo */}
        <Box className="relative flex items-center justify-center w-10 h-10 rounded-full p-4 overflow-hidden cursor-pointer" onClick={toggleChat}>
          <img src={wolfieLogo} alt="Wolfie Logo" className="w-10 h-10 relative z-10" />
        </Box>

        {/* Animated Audio Waves - Display only (disabled) */}
        {loading ? (
          <Box className="relative w-10 h-10 flex items-center justify-center rounded-full overflow-hidden bg-primaryIcy">
            <CircularProgress size={16} color="primary" />
          </Box>
        ) : (
          <WolfTooltip placement="top" title={localeWolfie['featureDisabled'] || 'Feature disabled'}>
            <Box
              className="relative w-10 h-6 flex items-center justify-center border border-frozenGrayShades8 border-solid rounded-full overflow-hidden opacity-50"
              sx={{ cursor: 'not-allowed' }}>
              <Box className="absolute z-10 w-full h-px bg-frozenGrayShades6 transform rotate-12 top-1/2"
                sx={{
                  left: '10%',
                  width: '80%',
                }}
              />
              <Box className="flex items-end gap-px2 h-6 w-6" sx={{ opacity: 0.3 }}>
                <Box className="h-2 w-px bg-white" sx={{ height: '8px' }} />
                <Box className="h-4 w-px bg-white" sx={{ height: '10px' }} />
                <Box className="h-3 w-px bg-white" sx={{ height: '16px' }} />
                <Box className="h-2 w-px bg-white" sx={{ height: '6px' }} />
                <Box className="h-5 w-px bg-white" sx={{ height: '16px' }} />
                <Box className="h-3 w-px bg-white" sx={{ height: '8px' }} />
                <Box className="h-2 w-px bg-white" sx={{ height: '12px' }} />
                <Box className="h-5 w-px bg-white" sx={{ height: '8px' }} />
              </Box>
            </Box>
          </WolfTooltip>
        )}
      </Box>

      <Box className="flex items-center gap-2 bg-primaryDark rounded-full p-2">
        {/* Microphone Toggle - Display only (disabled) */}
        {/* TODO: Move to external component all voice handling */}
        {/* <VoiceRecorder listening={listening} onSubmit={onSubmit} /> */}

        <WolfTooltip title={localeWolfie['featureDisabled'] || 'Feature disabled'}>
          <span> 
            <IconButton
              disabled
              className="rounded-full p-2 !text-white !bg-frozenGrayShades8 opacity-50"
              sx={{ cursor: 'not-allowed !important' }}>
              <MicOffIcon24 />
            </IconButton>
          </span>
        </WolfTooltip>

        {/* Chat Toggle */}
        <Box className="relative">
          <WolfTooltip title={isChatMinimized ? localeWolfie['showMessageWindow'] : localeWolfie['hideMessageWindow']}>
            <IconButton
              onClick={toggleChat}
              className="!bg-frozenGrayShades8 hover:bg-frozenGrayShades5 rounded-full p-2 !text-white cursor-pointer">
              {!isChatMinimized ? <ChevronRight className="rotate-90" /> : <KeywordIcon24 />}
            </IconButton>
          </WolfTooltip>
          {newMessage && (
            <Box className="absolute top-0 -right-1 w-4 h-4 rounded-full bg-sectionMediumSeaGreen animate-pulse border-primaryDark border-4 border-solid" />
          )}
        </Box>

        {/* User Avatar */}
        <Box className="w-10 h-10 rounded-full overflow-auto">
          <WolfAvatar src={avatar || ''} alt={firstName || ''} size="40px" />
        </Box>
      </Box>
    </Box>
  );
};

export default WolfieHub;
